import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import http from '../utils/http';
import { Address } from "./AddressSlice";
import { MessageEvent as MEvent } from './EventsSlice';
import { RootState } from './store';




export interface ContactNumber {
    number: string;
    extension: string;
}
export interface ContactInfo {
    number: ContactNumber;
    email: string;
}

export interface Auditor {
    name: string;
    code: string;
    branchId: string;
    address: Address;
    info: ContactInfo;
}

export interface AuditorReference {
    name: string;
    code: string;
    branchId: string;
}

export interface AddressInput {
    street: string;
    pobox: string;
    cityId: string;
    zip: string;
}
export interface ContactInfoInput {
    phoneNumber: string;
    email: string;
    extension: string | null;
}
export interface AuditorDetailsInput {
    code: string;
    name: string;  
    address: AddressInput;   
    contactInfo: ContactInfoInput;
}

export interface AuditorState {
    selectedAuditor: Auditor | null;
    pendingAuditors: Array<MEvent>;
    status: 'idle' | 'loading' | 'failed';
}

const initialState: AuditorState = {
    selectedAuditor: null,
    pendingAuditors: [],
    status: "idle"
}

export const loadAuditor = createAsyncThunk(
    "Auditor/GetAuditorDetails",
    async (auditorCode:string | null): Promise<Auditor | null> => {
        try {
            if (auditorCode) {
                const result = await http<string, Auditor>({
                    path: `/auditor/loadAuditor`,
                    method: 'post',
                    body: auditorCode
                });
                if (result.ok && result.parsedBody) {
                    return result.parsedBody;
                }
                else {
                    return null;
                }
            }
            else {
                return null;
            }           
        } catch (ex) {
            return null;
        }
    });

export const saveAuditor = createAsyncThunk(
    "Auditor/SaveAuditor",
    async (newAuditor: AuditorDetailsInput): Promise<Auditor | null> => {
        try {
            const result = await http<AuditorDetailsInput, Auditor>({
                path: `/auditor/saveAuditor`,
                method: 'post',
                body: newAuditor
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const getAllAuditorRequests = createAsyncThunk(
    "Auditor/GetAllAuditorRequests",
    async (): Promise<MEvent | null> => {
        try {
            const result = await http<null, MEvent>({
                path: `/events/getAllPendingAuditors`,
                method: 'get',

            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const createAuditor = createAsyncThunk(
    "Auditor/CreateAuditor",
    async (taxonomyIdNewAuditor: { item1: number; item2: AuditorDetailsInput }): Promise<Auditor | null> => {
        try {
            const result = await http<{ item1: number; item2: AuditorDetailsInput }, Auditor>({
                path: `/auditor/createAuditor`,
                method: 'post',
                body: taxonomyIdNewAuditor
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const rejectPendingAuditor = createAsyncThunk(
    "Auditor/RejectPendingAuditor",
    async (entityIdAuditorName: { item1: string; item2: string }): Promise<MEvent | null> => {
        try {
            const result = await http<{ item1: string; item2: string }, MEvent>({
                path: `/auditor/rejectAuditor`,
                method: 'post',
                body: entityIdAuditorName
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (e) {
            return null
        }
    });

export const getNewAuditorMessageEvents = createAsyncThunk(
    "Auditor/GetNewAuditorMessageEvents",
    async (afrReference: string): Promise<Array<MEvent> | null> => {
        try {
            const result = await http<string, Array<MEvent> | null>({
                path: `/auditor/getMessageEventsGivenEntity/${afrReference}`,
                method: 'get',
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const auditorSlice = createSlice({
    name: 'Auditor',
    initialState,
    reducers: {
        resetAuditorState: (state: any, action: any) => {
            return initialState
        },
        setToSelectedAuditor: (state: any, action: any) => {
            state.selectedAuditor = action.payload;
        }
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(loadAuditor.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadAuditor.fulfilled, (state: any, action: any) => {
                state.selectedAuditor = action.payload;
                state.status = "idle";
            })
            .addCase(saveAuditor.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(saveAuditor.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.selectedAuditor = null;
            })
            .addCase(getAllAuditorRequests.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getAllAuditorRequests.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.pendingAuditors = [];
                for (var i = 0; i < action.payload.length; i++) {
                    if (action.payload[i].role == "REQUEST_AUDITOR") {
                        var newMessageEvent = {
                            connector: action.payload[i].connector,
                            status: 'message',
                            creator: action.payload[i].creator,
                            message: action.payload[i].message,
                            role: action.payload[i].role,
                            timestamp: action.payload[i].timestamp
                        }

                        state.pendingAuditors.push(newMessageEvent);
                    }
                }                
            })
            .addCase(getNewAuditorMessageEvents.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getNewAuditorMessageEvents.fulfilled, (state: any, action: any) => {
                state.status = "idle";

                if (action.payload) {
                    state.pendingAuditors = [];

                    for (var i = 0; i < action.payload.length; i++) {
                        
                        var newMessageEvent = {
                            connector: action.payload[i].connector,
                            status: 'message',
                            creator: action.payload[i].creator,
                            message: action.payload[i].message,
                            role: action.payload[i].role,
                            timestamp: action.payload[i].timestamp
                        }

                        state.pendingAuditors.push(newMessageEvent);                        
                    }
                }              
            })
    },
});

export const { resetAuditorState, setToSelectedAuditor } = auditorSlice.actions;

export const selectAuditor = (state: RootState) => state.auditor ? state.auditor.selectedAuditor : null;

export const selectPendingAuditors = (state: RootState) => state.auditor ? state.auditor.pendingAuditors : null;

export default auditorSlice.reducer;